import React, { useMemo } from "react";

import type { MeetingStatus } from "@performyard/models";
import { AvatarGroup, Chip, Text } from "@performyard/py-components";

import ExpandableText from "./ExpandableText";
import MeetingCalendarIcon from "./MeetingCalendarIcon";
import MeetingOptionsDropdown from "./MeetingOptionsDropdown";
import PreMeetingPrepButton from "./PreMeetingPrepButton";
import PreMeetingPrepPanel from "./PreMeetingPrepPanel";

import { usePreMeetingPrep } from "../hooks";

import { toAvatarGroupEmployees } from "../../utils/participants";
import { formatMeetingDateTimeDisplay, getParticipantName } from "../utils";

import { MEETING_STATUS_CONFIG, config } from "../config";
import { useMeetingContext } from "../context";

/**
 * Header section displaying meeting details: name, participants, status, and options.
 */
const MeetingDetailsHeader = (): React.ReactElement => {
	const { meeting, userId, setEditingMeeting, isAdminView, workspace, participantNames } = useMeetingContext();
	const preMeetingPrep = usePreMeetingPrep();

	const hasDescription = Boolean(meeting.description?.trim());
	const isTeamMeeting = meeting.participants.length > 2;

	const participantEmployees = useMemo(
		() =>
			toAvatarGroupEmployees(meeting.participants, {
				currentUserId: userId,
				resolveName: (id) => getParticipantName(workspace, participantNames, id),
			}),
		[meeting.participants, userId, workspace, participantNames]
	);

	const getStatusChip = (status: MeetingStatus): React.ReactElement => {
		const { label, color } = MEETING_STATUS_CONFIG[status];
		return <Chip label={label} color={color} size={24} />;
	};

	const renderMeetingDateTime = (): React.ReactElement => {
		const { dateStr, timeStr, isMultiDay } = formatMeetingDateTimeDisplay(
			meeting.startDateTime,
			meeting.endDateTime
		);

		if (isMultiDay) {
			return (
				<Text size="paragraph-3" weight="regular">
					{dateStr}
				</Text>
			);
		}

		return (
			<Text size="paragraph-3" weight="regular">
				{dateStr} • {timeStr}
			</Text>
		);
	};

	const showPreMeetingPrep = preMeetingPrep.isVisible && !isTeamMeeting;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-1">
				<div className="flex items-center gap-2">
					<Text size="heading-5" weight="semi-bold" dangerouslySetClassName="py-line-clamp-1">
						{meeting.name}
					</Text>
					<AvatarGroup
						employees={participantEmployees}
						hasLinks={true}
						maxLength={participantEmployees.length <= 5 ? 5 : 4}
						size={config.layout.dropdownAvatarSize}
						employeeNameTooltip={true}
						modalTitle="Meeting Participants"
					/>
					{meeting.status !== "active" && (
						<div className="flex-shrink-0">{getStatusChip(meeting.status)}</div>
					)}
					{!isAdminView && (
						<div className="ml-auto flex items-center gap-2">
							{showPreMeetingPrep && <PreMeetingPrepButton onClick={preMeetingPrep.handleOpen} />}
							<MeetingOptionsDropdown meeting={meeting} setEditingMeeting={setEditingMeeting} />
						</div>
					)}
				</div>
				<div className="flex items-center gap-1">
					<MeetingCalendarIcon meeting={meeting} />
					{renderMeetingDateTime()}
				</div>
			</div>

			{hasDescription && (
				<ExpandableText content={meeting.description || ""} collapseHeight={config.descriptionCollapseHeight} />
			)}

			<PreMeetingPrepPanel {...preMeetingPrep} />
		</div>
	);
};

export default MeetingDetailsHeader;
